import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type {
  ChartAxisLabelMode,
  ChartColorPaletteId,
  ChartLegendMode,
  ChartLegendPlacement,
  DashboardTabDefinition,
  DashboardVizKind,
  DashboardWidgetInstance,
  DashboardWidgetType,
  IndicatorFilterOperator,
  WidgetGroupDirection,
  WidgetStackNavStyle,
} from './types';
import { isVizWidgetType } from './widgetGuards';
import { useWebMapContext } from '../webMap/WebMapContext';

import {
  SettingsColorPicker,
  SettingsSlider,
  SettingsOpacitySlider,
  SettingsSelect,
  SettingsPalettePicker,
  SettingsToggleGroup,
} from './controls';

const VIZ_KIND_OPTIONS: { value: DashboardVizKind; label: string }[] = [
  { value: 'bar', label: 'Bar chart' },
  { value: 'line', label: 'Line chart' },
  { value: 'area', label: 'Area chart' },
  { value: 'donut', label: 'Donut / pie' },
  { value: 'radar', label: 'Radar chart' },
  { value: 'scatter', label: 'Scatter plot' },
  { value: 'treemap', label: 'Treemap' },
  { value: 'calendar', label: 'Calendar heatmap' },
  { value: 'text', label: 'Text note' },
];

export function ChartWidgetDataPanel(props: {
  widgetType: DashboardWidgetType;
  vizTabs: DashboardTabDefinition[];
  setVizTabs: React.Dispatch<React.SetStateAction<DashboardTabDefinition[]>>;
  heatmapWeeks: number;
  setHeatmapWeeks: (v: number) => void;
  heatmapCellSizeInput: string;
  setHeatmapCellSizeInput: (v: string) => void;
}): JSX.Element | null {
  const {
    widgetType,
    vizTabs,
    setVizTabs,
    heatmapWeeks,
    setHeatmapWeeks,
    heatmapCellSizeInput,
    setHeatmapCellSizeInput,
  } = props;

  const standaloneChart =
    widgetType === 'chart_bar' ||
    widgetType === 'chart_line' ||
    widgetType === 'chart_area' ||
    widgetType === 'chart_donut' ||
    widgetType === 'chart_radar' ||
    widgetType === 'chart_scatter' ||
    widgetType === 'chart_treemap' ||
    widgetType === 'gauge' ||
    widgetType === 'calendar_heatmap';

  if (!isVizWidgetType(widgetType) && !standaloneChart) return null;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Configure the data source in the Data tab by binding to a Web Map layer.
      </Typography>

      {widgetType === 'viz_tabs' && (
        <>
          <Typography variant="subtitle2">Tabs</Typography>
          {vizTabs.map((row, idx) => (
            <Stack key={row.id} direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
              <TextField
                label="Tab label"
                size="small"
                value={row.label}
                onChange={(e) => {
                  const next = [...vizTabs];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setVizTabs(next);
                }}
                sx={{ minWidth: 120, flex: 1 }}
              />
              <SettingsSelect
                label="View"
                value={row.viz}
                onChange={(v) => {
                  const next = [...vizTabs];
                  next[idx] = { ...next[idx], viz: v as DashboardVizKind };
                  setVizTabs(next);
                }}
                options={VIZ_KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                fullWidth={false}
              />
              <IconButton
                size="small"
                aria-label="Remove tab"
                onClick={() => setVizTabs(vizTabs.filter((_, i) => i !== idx))}
                disabled={vizTabs.length <= 1}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
              {row.viz === 'text' && (
                <TextField
                  label="Tab text"
                  size="small"
                  fullWidth
                  value={row.textBody ?? ''}
                  onChange={(e) => {
                    const next = [...vizTabs];
                    next[idx] = { ...next[idx], textBody: e.target.value };
                    setVizTabs(next);
                  }}
                />
              )}
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setVizTabs([
                ...vizTabs,
                { id: `t_${Date.now()}`, label: 'New tab', viz: 'bar' },
              ])
            }
          >
            Add tab
          </Button>
        </>
      )}

      {(widgetType === 'calendar_heatmap' || widgetType === 'viz_tabs') && (
        <>
          <TextField
            label="Calendar: weeks of history"
            type="number"
            size="small"
            inputProps={{ min: 4, max: 12 }}
            value={heatmapWeeks}
            onChange={(e) => setHeatmapWeeks(Math.min(12, Math.max(4, Number(e.target.value) || 6)))}
          />
          <Box>
            {(() => {
              const raw = heatmapCellSizeInput.trim();
              const n = raw === '' ? NaN : Number(raw);
              const sliderValue =
                raw === '' || !Number.isFinite(n)
                  ? 0
                  : Math.min(96, Math.max(4, Math.round(n)));
              return (
                <SettingsSlider
                  label="Calendar tile size"
                  value={sliderValue}
                  onChange={(v) => {
                    if (v <= 0) setHeatmapCellSizeInput('');
                    else setHeatmapCellSizeInput(String(Math.max(4, Math.min(96, v))));
                  }}
                  min={0}
                  max={96}
                  unit="px"
                  autoValue={0}
                  autoLabel="Auto"
                  marks={[
                    { value: 0, label: 'Auto' },
                    { value: 96, label: '96px' },
                  ]}
                />
              );
            })()}
            <Typography variant="caption" color="text.disabled">
              Left = fit to tile (compact grid). Right = larger fixed squares (4–96px).
            </Typography>
          </Box>
        </>
      )}
    </Stack>
  );
}

export function ChartOptionsPanel(props: {
  widgetType: DashboardWidgetType;
  chartTitle: string;
  setChartTitle: (v: string) => void;
  donutInnerRadiusPercent: number;
  setDonutInnerRadiusPercent: (v: number) => void;
  donutStartAngle: number;
  setDonutStartAngle: (v: number) => void;
  legendMode: ChartLegendMode;
  setLegendMode: (v: ChartLegendMode) => void;
  legendPlacement: ChartLegendPlacement;
  setLegendPlacement: (v: ChartLegendPlacement) => void;
  chartLabelFontSize: number;
  setChartLabelFontSize: (v: number) => void;
  chartTextColor: string;
  setChartTextColor: (v: string) => void;
  chartAxisLabelMode: ChartAxisLabelMode;
  setChartAxisLabelMode: (v: ChartAxisLabelMode) => void;
}): JSX.Element | null {
  const {
    widgetType,
    chartTitle,
    setChartTitle,
    donutInnerRadiusPercent,
    setDonutInnerRadiusPercent,
    donutStartAngle,
    setDonutStartAngle,
    legendMode,
    setLegendMode,
    legendPlacement,
    setLegendPlacement,
    chartLabelFontSize,
    setChartLabelFontSize,
    chartTextColor,
    setChartTextColor,
    chartAxisLabelMode,
    setChartAxisLabelMode,
  } = props;

  const showSubtitle =
    widgetType === 'chart_bar' ||
    widgetType === 'chart_line' ||
    widgetType === 'chart_area' ||
    widgetType === 'chart_donut' ||
    widgetType === 'chart_radar' ||
    widgetType === 'chart_scatter' ||
    widgetType === 'chart_treemap' ||
    widgetType === 'gauge' ||
    widgetType === 'calendar_heatmap';

  const showDonutControls = widgetType === 'chart_donut' || widgetType === 'chart_radar' || widgetType === 'viz_tabs';
  const showTypography = isVizWidgetType(widgetType) || showSubtitle;
  const showAxisLabels =
    widgetType === 'chart_bar'
    || widgetType === 'chart_line'
    || widgetType === 'chart_area'
    || widgetType === 'viz_tabs';

  if (!showSubtitle && !showDonutControls && !showTypography) return null;

  return (
    <Stack spacing={2}>
      {widgetType === 'viz_tabs' && (
        <Typography variant="body2" color="text.secondary">
          Chart options apply to bar, line, donut, and calendar views inside your tabs (e.g. inner radius and legend
          affect the donut view).
        </Typography>
      )}

      {showSubtitle && (
        <TextField
          label="Chart subtitle (optional)"
          size="small"
          fullWidth
          value={chartTitle}
          onChange={(e) => setChartTitle(e.target.value)}
        />
      )}

      {showDonutControls && (
        <>
          <Typography variant="caption" color="text.secondary">
            Donut / Pie Chart
          </Typography>
          <SettingsSlider
            label="Inner radius"
            value={donutInnerRadiusPercent}
            onChange={setDonutInnerRadiusPercent}
            min={0}
            max={85}
            unit="%"
          />
          <SettingsSlider
            label="Start angle"
            value={donutStartAngle}
            onChange={setDonutStartAngle}
            min={0}
            max={360}
            unit="°"
          />
          <SettingsToggleGroup
            label="Legend"
            value={legendMode}
            onChange={(v) => setLegendMode(v as ChartLegendMode)}
            options={[
              { value: 'hidden', label: 'Hide' },
              { value: 'value', label: 'Value' },
              { value: 'percent', label: 'Percent' },
            ]}
          />
          <SettingsToggleGroup
            label="Legend placement"
            value={legendPlacement}
            onChange={(v) => setLegendPlacement(v as ChartLegendPlacement)}
            options={[
              { value: 'bottom', label: 'Bottom' },
              { value: 'side', label: 'Side' },
            ]}
          />
        </>
      )}

      {showTypography && (
        <>
          {showAxisLabels && (
            <SettingsSelect
              label="Category label layout"
              value={chartAxisLabelMode}
              onChange={(v) => setChartAxisLabelMode(v as ChartAxisLabelMode)}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'horizontal', label: 'Horizontal' },
                { value: 'rotate-45', label: 'Rotate 45°' },
                { value: 'rotate-90', label: 'Rotate 90°' },
                { value: 'stacked', label: 'Stack / wrap' },
              ]}
            />
          )}
          <SettingsSlider
            label="Chart label font size"
            value={chartLabelFontSize}
            onChange={setChartLabelFontSize}
            min={8}
            max={24}
            unit="px"
          />
          <SettingsColorPicker
            label="Chart text and axis color"
            value={chartTextColor}
            onChange={setChartTextColor}
            placeholder="Leave empty for theme default"
          />
        </>
      )}
    </Stack>
  );
}

export function SlicesPalettePanel(props: {
  chartColorPaletteId: ChartColorPaletteId;
  setChartColorPaletteId: (v: ChartColorPaletteId) => void;
}): JSX.Element {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Choose a color ramp for bars and donut slices.
      </Typography>
      <SettingsPalettePicker
        value={props.chartColorPaletteId}
        onChange={props.setChartColorPaletteId}
      />
    </Stack>
  );
}

export function AppearancePanel(props: {
  cardBackground: string;
  setCardBackground: (v: string) => void;
  cardBorderColor: string;
  setCardBorderColor: (v: string) => void;
  cardBorderRadius: number;
  setCardBorderRadius: (v: number) => void;
  cardOpacity: number;
  setCardOpacity: (v: number) => void;
  contentScale: number;
  setContentScale: (v: number) => void;
  titleColor: string;
  setTitleColor: (v: string) => void;
  titleFontSize: 'small' | 'medium' | 'large';
  setTitleFontSize: (v: 'small' | 'medium' | 'large') => void;
  contentTextColor: string;
  setContentTextColor: (v: string) => void;
  hideHeader: boolean;
  setHideHeader: (v: boolean) => void;
  hideLabel: boolean;
  setHideLabel: (v: boolean) => void;
}): JSX.Element {
  const {
    cardBackground,
    setCardBackground,
    cardBorderColor,
    setCardBorderColor,
    cardBorderRadius,
    setCardBorderRadius,
    cardOpacity,
    setCardOpacity,
    contentScale,
    setContentScale,
    titleColor,
    setTitleColor,
    titleFontSize,
    setTitleFontSize,
    contentTextColor,
    setContentTextColor,
    hideHeader,
    setHideHeader,
    hideLabel,
    setHideLabel,
  } = props;

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={<Switch checked={hideHeader} onChange={(e) => setHideHeader(e.target.checked)} />}
        label="Hide header"
      />
      <FormControlLabel
        control={<Switch checked={hideLabel} onChange={(e) => setHideLabel(e.target.checked)} />}
        label="Hide data-source label"
      />
      <SettingsColorPicker
        label="Card background"
        value={cardBackground}
        onChange={setCardBackground}
        placeholder="#1e1e1e or rgba(...)"
      />
      <SettingsColorPicker
        label="Border color"
        value={cardBorderColor}
        onChange={setCardBorderColor}
        placeholder="#444 or rgba(...)"
      />
      <SettingsSlider
        label="Corner radius"
        value={cardBorderRadius}
        onChange={setCardBorderRadius}
        min={0}
        max={24}
        unit="px"
      />
      <SettingsOpacitySlider
        label="Tile opacity"
        value={cardOpacity}
        onChange={setCardOpacity}
      />
      <Typography variant="caption" color="text.secondary">
        Content size scales what is inside the tile (map, charts, text). Resize the widget on the grid to change tile
        size.
      </Typography>
      <SettingsSlider
        label="Content size"
        value={Math.round(contentScale * 100)}
        onChange={(v) => setContentScale(v / 100)}
        min={50}
        max={150}
        step={5}
        unit="%"
        marks={[{ value: 75, label: '75%' }]}
      />
      <SettingsColorPicker
        label="Title color (header)"
        value={titleColor}
        onChange={setTitleColor}
        placeholder="#ffffff or rgba(...)"
      />
      <SettingsSelect
        label="Title size"
        value={titleFontSize}
        onChange={(v) => setTitleFontSize(v as 'small' | 'medium' | 'large')}
        options={[
          { value: 'small', label: 'Small' },
          { value: 'medium', label: 'Medium' },
          { value: 'large', label: 'Large' },
        ]}
      />
      <SettingsColorPicker
        label="Body text color (text and non-map content)"
        value={contentTextColor}
        onChange={setContentTextColor}
        placeholder="rgba(...) or hex"
      />
    </Stack>
  );
}

const INDICATOR_VALUE_SAMPLE_CAP = 400;

export function IndicatorSettingsPanel(props: {
  boundLayerId: string;
  availableFields: Array<{ name: string; type: string }>;
  indicatorFilterField: string;
  setIndicatorFilterField: (v: string) => void;
  indicatorFilterOperator: IndicatorFilterOperator;
  setIndicatorFilterOperator: (v: IndicatorFilterOperator) => void;
  indicatorFilterValue: string;
  setIndicatorFilterValue: (v: string) => void;
  indicatorTextTop: string;
  setIndicatorTextTop: (v: string) => void;
  indicatorTextMiddle: string;
  setIndicatorTextMiddle: (v: string) => void;
  indicatorTextBottom: string;
  setIndicatorTextBottom: (v: string) => void;
  indicatorColorTop: string;
  setIndicatorColorTop: (v: string) => void;
  indicatorColorMiddle: string;
  setIndicatorColorMiddle: (v: string) => void;
  indicatorColorBottom: string;
  setIndicatorColorBottom: (v: string) => void;
  indicatorAccent: string;
  setIndicatorAccent: (v: string) => void;
  indicatorFaceBg: string;
  setIndicatorFaceBg: (v: string) => void;
  indicatorBgMiddle: string;
  setIndicatorBgMiddle: (v: string) => void;
  indicatorShowLastUpdated: boolean;
  setIndicatorShowLastUpdated: (v: boolean) => void;
}): JSX.Element {
  const p = props;
  const webMapCtx = useWebMapContext();

  const resolvedLayerFields = useMemo(() => {
    if (p.availableFields.length > 0) return p.availableFields;
    if (!webMapCtx || !p.boundLayerId) return [];
    const feats = webMapCtx.layerFeatures.get(p.boundLayerId) ?? [];
    for (const feat of feats) {
      const pr = feat.properties;
      if (pr && typeof pr === 'object' && !Array.isArray(pr)) {
        return Object.keys(pr).map((name) => ({ name, type: 'from data' }));
      }
    }
    return [];
  }, [p.availableFields, webMapCtx, p.boundLayerId]);

  const useLayerPickers = Boolean(p.boundLayerId && resolvedLayerFields.length > 0);

  const fieldMenuOptions = useMemo(() => {
    const f = p.indicatorFilterField.trim();
    if (!f) return resolvedLayerFields;
    if (resolvedLayerFields.some((x) => x.name === f)) return resolvedLayerFields;
    return [...resolvedLayerFields, { name: f, type: 'unknown' }];
  }, [resolvedLayerFields, p.indicatorFilterField]);

  const distinctFilterValues = useMemo(() => {
    if (!webMapCtx || !p.boundLayerId || !p.indicatorFilterField) return [];
    const raw = webMapCtx.getFieldValues(p.boundLayerId, p.indicatorFilterField);
    const uniq = new Set<string>();
    for (const v of raw) {
      if (v == null) continue;
      uniq.add(String(v));
    }
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .slice(0, INDICATOR_VALUE_SAMPLE_CAP);
  }, [webMapCtx, p.boundLayerId, p.indicatorFilterField]);

  const valueOptions = useMemo(() => {
    const cur = p.indicatorFilterValue.trim();
    if (cur && !distinctFilterValues.includes(cur)) {
      return [cur, ...distinctFilterValues];
    }
    return distinctFilterValues;
  }, [distinctFilterValues, p.indicatorFilterValue]);

  const OPERATOR_OPTIONS = [
    { value: 'eq', label: 'Equal' },
    { value: 'neq', label: 'Not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Count features from the bound layer. Use <strong>{'{value}'}</strong> in any text line for the statistic.
        Leave the filter value empty to count all features in the bound layer.
      </Typography>
      {!useLayerPickers && (
        <Typography variant="caption" color="warning.main">
          Choose a Web Map layer on the <strong>Data</strong> tab first to pick filter field and values from the
          source. You can still type field and value manually below.
        </Typography>
      )}
      <Typography variant="subtitle2">Filter (optional)</Typography>
      {useLayerPickers ? (
        <SettingsSelect
          label="Filter field"
          value={p.indicatorFilterField}
          onChange={(v) => {
            p.setIndicatorFilterField(v);
            p.setIndicatorFilterValue('');
          }}
          options={[
            { value: '', label: 'None — count all features (no attribute filter)' },
            ...fieldMenuOptions.map((f) => ({ value: f.name, label: `${f.name} (${f.type})` })),
          ]}
        />
      ) : (
        <TextField
          label="Filter field (from bound layer)"
          size="small"
          fullWidth
          value={p.indicatorFilterField}
          onChange={(e) => p.setIndicatorFilterField(e.target.value)}
          helperText="Type the name of a field from the bound Web Map layer."
        />
      )}
      <SettingsSelect
        label="Operator"
        value={p.indicatorFilterOperator}
        onChange={(v) => p.setIndicatorFilterOperator(v as IndicatorFilterOperator)}
        options={OPERATOR_OPTIONS}
      />
      {p.indicatorFilterOperator !== 'is_empty' && p.indicatorFilterOperator !== 'is_not_empty' && (
        useLayerPickers && p.indicatorFilterField ? (
          <>
            <SettingsSelect
              label="Filter value"
              value={p.indicatorFilterValue}
              onChange={p.setIndicatorFilterValue}
              options={[
                { value: '', label: 'Any — no value filter' },
                ...valueOptions.map((v) => ({ value: v, label: v })),
              ]}
            />
            <Typography variant="caption" color="text.secondary">
              Distinct values from the bound layer (current map filters apply). Case-insensitive match when counting.
              {distinctFilterValues.length >= INDICATOR_VALUE_SAMPLE_CAP
                ? ` Showing up to ${INDICATOR_VALUE_SAMPLE_CAP} values.`
                : null}
            </Typography>
          </>
        ) : (
          <TextField
            label="Value (e.g. vehicle, phone, Student)"
            size="small"
            fullWidth
            value={p.indicatorFilterValue}
            onChange={(e) => p.setIndicatorFilterValue(e.target.value)}
            helperText="Case-insensitive match. Empty = no filter."
          />
        )
      )}
      <Typography variant="subtitle2">Text</Typography>
      <TextField
        label="Top text (optional)"
        size="small"
        fullWidth
        value={p.indicatorTextTop}
        onChange={(e) => p.setIndicatorTextTop(e.target.value)}
      />
      <TextField
        label="Middle text"
        size="small"
        fullWidth
        value={p.indicatorTextMiddle}
        onChange={(e) => p.setIndicatorTextMiddle(e.target.value)}
        helperText="Example: Count: {value}"
      />
      <TextField
        label="Bottom text (optional)"
        size="small"
        fullWidth
        value={p.indicatorTextBottom}
        onChange={(e) => p.setIndicatorTextBottom(e.target.value)}
      />
      <Typography variant="subtitle2">Look</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SettingsColorPicker
          label="Middle text color"
          value={p.indicatorColorMiddle}
          onChange={p.setIndicatorColorMiddle}
          placeholder="#69f0ae"
        />
        <SettingsColorPicker
          label="Top text color"
          value={p.indicatorColorTop}
          onChange={p.setIndicatorColorTop}
        />
      </Stack>
      <SettingsColorPicker
        label="Bottom text color"
        value={p.indicatorColorBottom}
        onChange={p.setIndicatorColorBottom}
      />
      <Typography variant="subtitle2">Background &amp; accent</Typography>
      <SettingsColorPicker
        label="Accent (border glow / highlight)"
        value={p.indicatorAccent}
        onChange={p.setIndicatorAccent}
        placeholder="#00e5ff"
      />
      <SettingsColorPicker
        label="Face background (behind text)"
        value={p.indicatorFaceBg}
        onChange={p.setIndicatorFaceBg}
        placeholder="rgba(0,0,0,0.3)"
      />
      <SettingsColorPicker
        label="Middle text background"
        value={p.indicatorBgMiddle}
        onChange={p.setIndicatorBgMiddle}
        placeholder="transparent"
      />
      <FormControlLabel
        control={
          <Switch checked={p.indicatorShowLastUpdated} onChange={(e) => p.setIndicatorShowLastUpdated(e.target.checked)} />
        }
        label="Show last updated time"
      />
    </Stack>
  );
}

/* ------------------------------------------------------------------
 * DataBindingPanel — bind a widget to a web_map layer for data context
 * ------------------------------------------------------------------ */

export interface DataBindingPanelProps {
  boundMapWidgetId: string;
  setBoundMapWidgetId: (v: string) => void;
  boundLayerId: string;
  setBoundLayerId: (v: string) => void;
  field: string;
  setField: (v: string) => void;
  groupByField: string;
  setGroupByField: (v: string) => void;
  valueField: string;
  setValueField: (v: string) => void;
  aggregation: string;
  setAggregation: (v: string) => void;
  webMapWidgets: Array<{ id: string; title: string }>;
  availableLayers: Array<{ id: string; label: string }>;
  availableFields: Array<{ name: string; type: string }>;
  widgetType: DashboardWidgetType;
}

const AGGREGATION_OPTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
];

/* ------------------------------------------------------------------
 * StackSettingsPanel — navigation style + child reorder / remove
 * ------------------------------------------------------------------ */

const STACK_WIDGET_LABELS: Partial<Record<string, string>> = {
  placeholder: 'Placeholder',
  iframe: 'Embed',
  text: 'Text',
  viz_tabs: 'Charts',
  chart_bar: 'Bar',
  chart_line: 'Line',
  chart_area: 'Area',
  chart_donut: 'Donut',
  chart_radar: 'Radar',
  chart_scatter: 'Scatter',
  chart_treemap: 'Treemap',
  calendar_heatmap: 'Heatmap',
  gauge: 'Gauge',
  indicator: 'Indicator',
  data_table: 'Table',
  web_map: 'Map',
  spatial_analytics: 'Analytics',
};

function stackChildLabel(w: DashboardWidgetInstance | undefined, idx: number): string {
  if (!w) return `Layer ${idx + 1}`;
  if (w.config?.customTitle?.trim()) return w.config.customTitle.trim();
  if (w.type === 'text' && w.config?.title?.trim()) return w.config.title.trim();
  return STACK_WIDGET_LABELS[w.type] ?? `Layer ${idx + 1}`;
}

export function StackSettingsPanel(props: {
  childIds: string[];
  widgets: Record<string, DashboardWidgetInstance>;
  navStyle: WidgetStackNavStyle;
  setNavStyle: (v: WidgetStackNavStyle) => void;
  tabLabels?: Record<string, string>;
  onTabLabelChange?: (childId: string, label: string) => void;
  onReorder: (childIds: string[]) => void;
  onRemoveChild: (childId: string) => void;
}): JSX.Element {
  const { childIds, widgets, navStyle, setNavStyle, tabLabels = {}, onTabLabelChange, onReorder, onRemoveChild } = props;

  const moveChild = (idx: number, dir: -1 | 1) => {
    const arr = [...childIds];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    onReorder(arr);
  };

  return (
    <Stack spacing={2}>
      <SettingsToggleGroup
        label="Navigation style"
        value={navStyle}
        onChange={(v) => setNavStyle(v as WidgetStackNavStyle)}
        options={[
          { value: 'tabs', label: 'Tabs' },
          { value: 'carousel', label: 'Carousel' },
        ]}
      />

      <Typography variant="subtitle2">
        Layers ({childIds.length})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Drag widgets onto this stack to add more layers. Removing a layer puts it back on the grid.
      </Typography>

      {childIds.length === 0 && (
        <Typography variant="body2" color="text.disabled">
          No child widgets yet.
        </Typography>
      )}
      {childIds.map((cid, idx) => {
        const w = widgets[cid];
        return (
          <Stack key={cid} spacing={0.5} sx={{ '&:hover': { bgcolor: 'action.hover' }, px: 0.5, py: 0.5, borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'text.secondary' }}>
                {stackChildLabel(w, idx)}
              </Typography>
              <IconButton size="small" disabled={idx <= 0} onClick={() => moveChild(idx, -1)}>
                <ArrowUpwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" disabled={idx >= childIds.length - 1} onClick={() => moveChild(idx, 1)}>
                <ArrowDownwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => onRemoveChild(cid)} color="error">
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <TextField
              size="small"
              placeholder={stackChildLabel(w, idx)}
              value={tabLabels[cid] ?? ''}
              onChange={(e) => onTabLabelChange?.(cid, e.target.value)}
              fullWidth
              label="Tab name"
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
}

/* ------------------------------------------------------------------
 * GroupSettingsPanel — direction toggle + child reorder / remove
 * ------------------------------------------------------------------ */

function groupChildLabel(w: DashboardWidgetInstance | undefined, idx: number): string {
  if (!w) return `Member ${idx + 1}`;
  if (w.config?.customTitle?.trim()) return w.config.customTitle.trim();
  if (w.type === 'text' && w.config?.title?.trim()) return w.config.title.trim();
  return STACK_WIDGET_LABELS[w.type] ?? `Member ${idx + 1}`;
}

export function GroupSettingsPanel(props: {
  childIds: string[];
  widgets: Record<string, DashboardWidgetInstance>;
  direction: WidgetGroupDirection;
  setDirection: (v: WidgetGroupDirection) => void;
  onReorder: (childIds: string[]) => void;
  onRemoveChild: (childId: string) => void;
}): JSX.Element {
  const { childIds, widgets, direction, setDirection, onReorder, onRemoveChild } = props;

  const moveChild = (idx: number, dir: -1 | 1) => {
    const arr = [...childIds];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    onReorder(arr);
  };

  return (
    <Stack spacing={2}>
      <SettingsToggleGroup
        label="Layout direction"
        value={direction}
        onChange={(v) => setDirection(v as WidgetGroupDirection)}
        options={[
          { value: 'row', label: 'Side by side (row)' },
          { value: 'column', label: 'Stacked (column)' },
        ]}
      />

      <Typography variant="subtitle2">
        Members ({childIds.length})
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Ctrl+drag widgets onto an edge of this group to add more members. Removing a member puts it back on the grid.
      </Typography>

      {childIds.length === 0 && (
        <Typography variant="body2" color="text.disabled">
          No child widgets yet.
        </Typography>
      )}
      {childIds.map((cid, idx) => {
        const w = widgets[cid];
        return (
          <Stack key={cid} direction="row" alignItems="center" spacing={0.5} sx={{ '&:hover': { bgcolor: 'action.hover' }, px: 0.5, py: 0.25, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {groupChildLabel(w, idx)}
            </Typography>
            <IconButton size="small" disabled={idx <= 0} onClick={() => moveChild(idx, -1)}>
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" disabled={idx >= childIds.length - 1} onClick={() => moveChild(idx, 1)}>
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onRemoveChild(cid)} color="error">
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        );
      })}
    </Stack>
  );
}

export function DataBindingPanel(p: DataBindingPanelProps) {
  const isIndicator = p.widgetType === 'indicator';
  const isChart = ['chart_bar', 'chart_line', 'chart_area', 'chart_donut', 'chart_radar', 'chart_scatter', 'chart_treemap', 'gauge', 'calendar_heatmap', 'viz_tabs', 'data_table'].includes(p.widgetType);

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">Bind to Web Map layer</Typography>
      <Typography variant="body2" color="text.secondary">
        Connect this widget to a Web Map layer so it derives its data from the map's loaded features.
      </Typography>

      <SettingsSelect
        label="Web Map widget"
        value={p.boundMapWidgetId}
        onChange={(v) => {
          p.setBoundMapWidgetId(v);
          p.setBoundLayerId('');
          p.setField('');
        }}
        options={[
          { value: '', label: 'None — bind a Web Map to supply data' },
          ...p.webMapWidgets.map((w) => ({ value: w.id, label: w.title })),
        ]}
      />

      {p.boundMapWidgetId && (
        <SettingsSelect
          label="Layer"
          value={p.boundLayerId}
          onChange={(v) => {
            p.setBoundLayerId(v);
            p.setField('');
          }}
          options={[
            { value: '', label: 'Select layer' },
            ...p.availableLayers.map((l) => ({ value: l.id, label: l.label })),
          ]}
        />
      )}

      {p.boundLayerId && isIndicator && (
        <>
          <SettingsSelect
            label="Field"
            value={p.field}
            onChange={p.setField}
            options={[
              { value: '', label: 'Count features (no field)' },
              ...p.availableFields.map((f) => ({ value: f.name, label: `${f.name} (${f.type})` })),
            ]}
          />
          <SettingsSelect
            label="Aggregation"
            value={p.aggregation}
            onChange={p.setAggregation}
            options={AGGREGATION_OPTIONS}
          />
        </>
      )}

      {p.boundLayerId && isChart && (
        <>
          <SettingsSelect
            label={p.widgetType === 'calendar_heatmap' ? 'Date / time field' : 'Group by field'}
            value={p.groupByField}
            onChange={p.setGroupByField}
            options={[
              { value: '', label: 'Select field' },
              ...p.availableFields.map((f) => ({ value: f.name, label: `${f.name} (${f.type})` })),
            ]}
          />
          <SettingsSelect
            label="Value field (optional)"
            value={p.valueField}
            onChange={p.setValueField}
            options={[
              { value: '', label: 'Count per group' },
              ...p.availableFields
                .filter((f) => ['integer', 'bigint', 'numeric', 'real', 'double precision', 'smallint'].includes(f.type))
                .map((f) => ({ value: f.name, label: `${f.name} (${f.type})` })),
            ]}
          />
        </>
      )}
    </Stack>
  );
}
